<template>
  <label
    :class="[
      brandClasses.base,
      disabled ? 'bg-grey-90' : variant ? 'bg-brand-1' : 'bg-white',
      disabled
        ? 'c-grey-30 b-grey-60 forced-colors:bg-[GrayText]'
        : invalid && !focused
          ? (variant ? 'c-red-40 b-red-60' : 'c-red-40 b-red-30')
          : invalid && focused
            ? ['c-red-40 ring-1 ring-inset', (variant
              ? 'b-red-60 ring-red-60'
              : 'b-red-30 ring-red-30')]
            : focused
              ? !variant ? 'b-grey-20 ring-1 ring-inset ring-grey-20' : 'b-white'
              : !variant ? 'b-grey-40 ' : 'b-white',
      { 'cursor-pointer': !disabled },
    ]"
    data-test-id="vf-select"
    @focusin="focused = true"
    @focusout="focused = false"
  >

    <span
      class="pointer-events-none absolute left-3 flex items-center duration"
      :class="[
        modelValue || focused || placeholder ? brandClasses.labelFocused : 'top-0 bottom-0',
        labelClass,
        { 'form-label': modelValue },
        { 'c-grey-10': focused && !invalid && !variant },
      ]"
      data-test-id="vf-select-label"
    >
      <!-- @slot Slot is meant for `label` only -->
      <slot />
      <span v-if="required" aria-hidden="true"> *</span>
    </span>
    <base-select
      v-bind="{ options, placeholder, disabled, required }"
      v-model="modelValue"
      class="full flex items-center pl-3 pr-10 form-input"
      :class="[
        !disabled && (modelValue ? variant ? 'c-white' : 'c-grey-10' : 'c-grey-20'),
        modelValue || focused || placeholder ? brandClasses.selectFocused : '',
        textClass,
      ]"
      data-test-id="form-credit-card-select"
      @update:model-value="$emit('change', $event)"
    />
    <vf-icon
      class="pointer-events-none absolute inset-y-0 right-0 my-a mr-2 "
      :class="[disabled ? 'c-grey-50' : 'c-grey-10', iconClass]"
      dir="down"
      name="chevron"
      :size="iconSize || fallbackIconSize"
    />
  </label>
</template>

<script lang="ts" setup>
import type { CSSClass } from '#types/common'
import type { SelectOption } from '#types/components/base/select'
import type { IconSizes } from '#types/sizes'

defineProps<{
  /**
   * Defines list of options
   */
  options: (string | SelectOption)[] | (() => Promise<SelectOption[]>)
  /**
   * Defines placeholder when no selection
   */
  placeholder?: string
  /**
   * Defines disabled state
   */
  disabled?: boolean
  /**
   * Defines invalid state
   */
  invalid?: boolean
  /**
   * Defines if the select is required for accessibility purposes
   */
  required?: boolean
  /**
   * Defines variant
   */
  variant?: 'inverse'
  /**
   * Defines class the needed to be added to icon
   */
  iconClass?: CSSClass
  /**
   * Defines icon size
   */
  iconSize?: IconSizes
  /**
   * Defines class the needed to be added to text and base select
   */
  textClass?: CSSClass
  /**
   * Defines class the needed to be added to label and base select
   */
  labelClass?: CSSClass
}>()

defineEmits<{ change: [value?: string | number | object] }>()

const modelValue = defineModel<string | number | object>()
const focused = ref(false)

const { brandClasses, iconSize: fallbackIconSize } = useAppConfig().components.vf.select
</script>
